import React, {useContext, useEffect, useState, useRef, useCallback} from 'react';
import MyContext from "../../../Context";
import {useTranslation} from "react-i18next";
import ReactFlow, { addEdge, applyEdgeChanges, applyNodeChanges, Background, Controls } from 'reactflow';
import { useSearchParams } from 'react-router-dom';

import OperationNode from '../../../nodes/OperationNode.js'
import ValueNode from "../../../nodes/ValueNode";
import ActionNode from "../../../nodes/ActionNode";
import TimerNode from "../../../nodes/TimerNode";
import DelayNode from "../../../nodes/DelayNode";

import 'reactflow/dist/style.css'
import axios from '../../../AxiosInstance';
import OperationsSidebar from "../../../components/OperationsSidebar";
import OperationsHeader from "../../../components/OperationsHeader";
import OperationsToolbar from "../components/toolbar/toolbar";
import { validateAllActions, getConnectedOperations, isComparisonOperator } from "../../../utils/operationValidation";
import styles from './operations.module.css';

const nodeTypes = { operation: OperationNode, value: ValueNode, action: ActionNode, timer: TimerNode, delay: DelayNode };

export default function Operations() {
    const [searchParams] = useSearchParams();
    // Leer de URL primero, fallback a localStorage para evitar race condition
    // Validar que no sea null, undefined, o el string "undefined"
    const urlGroupId = searchParams.get('group');
    let storedGroupId = localStorage.getItem('operation_group_id');

    // Limpiar localStorage si tiene un valor inválido
    if (storedGroupId === 'undefined' || storedGroupId === 'null') {
        localStorage.removeItem('operation_group_id');
        storedGroupId = null;
    }

    const operation_group_id = (urlGroupId && urlGroupId !== 'undefined' && urlGroupId !== 'null')
        ? urlGroupId
        : storedGroupId;
    const proOptions = { hideAttribution: false };

    const [nodes, setNodes] = useState([]);
    const [edges, setEdges] = useState([]);
    const [deletionList, setDeletionList] = useState([]);
    const [edgeDeletionList, setEdgeDeletionList] = useState([]);
    const [fetched, setFetched] = useState(false);
    const [deletionHandle, setDeletionHandle] = useState(-1);
    const project = JSON.parse(localStorage.getItem('project'));
    const [layouts, setLayouts] = useState([]);
    const [currentLayout, setCurrentLayout] = useState(null);
    const [showSaved, setShowSaved] = useState(false);
    const [handleNewNode, setHandleNewNode] = useState(0);
    const {t} = useTranslation();
    const {corporateGroup} = useContext(MyContext);
    const [testAllLoading, setTestAllLoading] = useState(false);
    const nodesRef = useRef(nodes);
    const edgesRef = useRef(edges);
    const isMountedRef = useRef(true);
    const [connectionVersion, setConnectionVersion] = useState(0);
    const [validationErrors, setValidationErrors] = useState([]);
    const [notification, setNotification] = useState({ show: false, type: '', message: '' });

    // Sincronizar localStorage cuando se lee de URL (solo si es un ID válido)
    useEffect(() => {
        if (operation_group_id && operation_group_id !== 'undefined' && operation_group_id !== 'null') {
            localStorage.setItem('operation_group_id', operation_group_id);
        }
    }, [operation_group_id]);

    // Cleanup al desmontar el componente
    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    // Mantener las refs actualizadas con los valores actuales
    useEffect(() => {
        nodesRef.current = nodes;
    }, [nodes]);

    useEffect(() => {
        edgesRef.current = edges;
    }, [edges]);

    // Function to get operations connected to a specific action node
    const getConnectedOperationsForAction = useCallback((actionNodeId) => {
        return getConnectedOperations(actionNodeId, edgesRef.current, nodesRef.current);
    }, []);

    // Función para obtener variables de resultado (solo las que tienen nombre guardado)
    const getResultVariables = () => {
        return nodesRef.current
            .filter(n => (n.type === 'operation' || n.type === 'timer' || n.type === 'delay') && n.data.result_name)
            .map((n) => {
                // Determinar el tipo de variable basándose en la operación
                // Comparaciones (>, <, =, etc.) → booleano (5)
                // Aritméticas (+, -, x, ÷) → float (2)
                const isComparison = isComparisonOperator(n.data.operation);
                const var_type = isComparison ? 5 : 2; // 5 = bool, 2 = float

                return {
                    text: n.data.result_name + " (result_" + n.id + ")",
                    value: `result_${n.id}`,
                    var_type: var_type
                };
            });
    };

    useEffect(() => {
        // Validación estricta: solo hacer la llamada si tenemos un ID válido
        if (!operation_group_id || operation_group_id === 'undefined' || operation_group_id === 'null') {
            console.log('No valid operation_group_id, skipping API call');
            return;
        }
        axios.get('operation_layout/?group=' + operation_group_id).then((response) => {
            const results = response.data || [];
            setLayouts(results);
            if (results.length > 0) {
                setCurrentLayout(results[0].id);
            } else {
                // Si no hay layouts, crear uno por defecto automáticamente
                axios.post('operation_layout/', {
                    name: 'Main Layout',
                    group: operation_group_id
                }).then((createResponse) => {
                    const newLayout = createResponse.data;
                    setLayouts([newLayout]);
                    setCurrentLayout(newLayout.id);
                    console.log('Created default layout:', newLayout);
                }).catch((err) => {
                    console.error('Error creating default layout:', err);
                });
            }
        });
    }, [operation_group_id]);

    useEffect(() => {
        if(!currentLayout) return;
        axios.get(`operation/?layout=${currentLayout}`).then((response) => {
            const loadedEdges = response.data['edges'] || [];
            const loadedNodes = response.data['nodes'] || [];

            // First set edges so they're available when nodes render
            setEdges(loadedEdges);

            // Update edgesRef immediately so it's available for validation
            edgesRef.current = loadedEdges;

            const nodesWithProps = loadedNodes.map((node) => {
                // Add the desired prop inside the 'data' property of each node
                return {
                    ...node,
                    data: {
                        ...node.data,
                        setNodes: setNodes,
                        setDeletionList: setDeletionList,
                        onDeleteNode: onDeleteNode,
                        getResultVariables: getResultVariables,
                        getConnectedOperations: getConnectedOperationsForAction,
                        connectionVersion: 1, // Initial version after load
                    },
                };
            });

            // Update nodesRef immediately
            nodesRef.current = nodesWithProps;

            setNodes(nodesWithProps);
            setConnectionVersion(1); // Trigger validation after load
            setFetched(true);
        });
    }, [currentLayout]);

    const onDeleteNode = (nodeId) => {
        // Find outgoing edges from this node and clear target operation data
        setEdges((prevEdges) => {
            const outgoing = prevEdges.filter((e) => e.source === nodeId);
            outgoing.forEach((edge) => {
                const inputId = edge.targetHandle;
                if (inputId) {
                    setNodes((prevNodes) =>
                        prevNodes.map((node) => {
                            if (node.id !== edge.target) return node;
                            let newData = { ...node.data };
                            if (inputId[1] === '1') {
                                newData.value_1 = 0;
                                newData.is_connected_1 = false;
                                newData.connected_type_1 = null;
                            } else if (inputId[1] === '2') {
                                newData.value_2 = 0;
                                newData.is_connected_2 = false;
                                newData.connected_type_2 = null;
                            }
                            return { ...node, data: newData };
                        })
                    );
                }
            });
            // Remove all edges connected to this node
            return prevEdges.filter((e) => e.source !== nodeId && e.target !== nodeId);
        });

        // Remove the node itself
        setNodes((prev) => prev.filter((node) => node.id !== nodeId));
        setDeletionList((prev) => [...prev, nodeId]);
    };

    const onNodesChange = (elements) => {
        elements.forEach((element) => {
            if(element.type === 'remove') {
                // Buscar el nodo que se está eliminando
                const nodeToDelete = nodes.find(n => n.id === element.id);

                // Si es un action node con widget asociado, eliminar el widget del dashboard
                if (nodeToDelete?.type === 'action' &&
                    nodeToDelete?.data?.action_type === '2' &&
                    nodeToDelete?.data?.action_data_5) {
                    axios.delete(`dashboard_widget/${nodeToDelete.data.action_data_5}/`)
                        .then(() => console.log('Widget deleted successfully:', nodeToDelete.data.action_data_5))
                        .catch((err) => console.error('Error deleting widget:', err));
                }

                // Find edges where this node is the source and clear target operation data
                const outgoingEdges = edges.filter((e) => e.source === element.id);
                outgoingEdges.forEach((edge) => {
                    const inputId = edge.targetHandle;
                    if (inputId) {
                        setNodes((prevNodes) =>
                            prevNodes.map((node) => {
                                if (node.id !== edge.target) return node;
                                let newData = { ...node.data };
                                if (inputId[1] === '1') {
                                    newData.value_1 = 0;
                                    newData.is_connected_1 = false;
                                    newData.connected_type_1 = null;
                                } else if (inputId[1] === '2') {
                                    newData.value_2 = 0;
                                    newData.is_connected_2 = false;
                                    newData.connected_type_2 = null;
                                }
                                return { ...node, data: newData };
                            })
                        );
                    }
                });

                // Remove connected edges
                const newEdges = edges.filter((e) => !(e.source === element.id || e.target === element.id));
                setEdges(newEdges);

                setDeletionList((prevList) => [
                    ...prevList,
                    element.id
                ]);
                setDeletionHandle(element.id);
            }
        });
        setNodes((nds) => applyNodeChanges(elements, nds));
    }

    const handleConnect = (connection) => {
        const sourceNode = nodes.find((n) => n.id === connection.source);
        const targetNode = nodes.find((n) => n.id === connection.target);

        if (!sourceNode || !targetNode) return;

        // Determinar el label y tipo a usar según el tipo de nodo fuente
        let sourceLabel = null;
        let sourceDataType = 'number'; // default
        if (sourceNode.type === 'value') {
            sourceLabel = sourceNode.data.label;
            // Value nodes carry variable data_type if available
            sourceDataType = sourceNode.data.data_type || 'number';
        } else if (sourceNode.type === 'operation' || sourceNode.type === 'timer' || sourceNode.type === 'delay') {
            sourceLabel = sourceNode.data.result_name || `Op_${sourceNode.id}`;
            // Comparison operators produce boolean output
            sourceDataType = isComparisonOperator(sourceNode.data.operation) ? 'boolean' : 'number';
        } else if (sourceNode.type === 'action' && connection.sourceHandle === 'out_api') {
            sourceLabel = t('operations.sandbox.apiStatus');
            sourceDataType = 'number';
        }

        // Timer only accepts boolean inputs (from comparison operations or boolean variables)
        if (targetNode.type === 'timer' && sourceDataType !== 'boolean') {
            setNotification({
                show: true,
                type: 'error',
                message: t('operations.sandbox.timerBooleanOnly')
            });
            return;
        }

        // Solo actualizar is_connected si el target es una operación, timer o delay
        if (sourceLabel !== null && (targetNode.type === 'operation' || targetNode.type === 'timer' || targetNode.type === 'delay')) {
            if(connection.targetHandle && connection.targetHandle[1] === '1') {
                setNodes((prevNodes) => {
                    return prevNodes.map((n) => {
                        if(n.id === targetNode.id) {
                            return {
                                ...n,
                                data: {
                                    ...n.data,
                                    value_1: sourceLabel,
                                    is_connected_1: true,
                                    connected_type_1: sourceDataType
                                },
                            };
                        }
                        return n;
                    });
                });
            } else if (connection.targetHandle && connection.targetHandle[1] === '2') {
                setNodes((prevNodes) => {
                    return prevNodes.map((n) => {
                        if(n.id === targetNode.id) {
                            return {
                                ...n,
                                data: {
                                    ...n.data,
                                    value_2: sourceLabel,
                                    is_connected_2: true,
                                    connected_type_2: sourceDataType
                                },
                            };
                        }
                        return n;
                    });
                });
            }
        }

        // Remove previous edge on same target handle if target is operation, timer or delay
        let newEdges = edges;
        if (targetNode.type === 'operation' || targetNode.type === 'timer' || targetNode.type === 'delay') {
            newEdges = edges.filter((edge) => edge.targetHandle !== connection.targetHandle);
        }

        // Make edge smooth
        connection.type = 'smoothstep';

        setEdges((prevEdges) => addEdge(connection, newEdges));

        // Update connection version to trigger validation refresh in action nodes
        setConnectionVersion(prev => prev + 1);

        // Update nodes with new connection version to trigger re-validation
        setTimeout(() => {
            setNodes(prevNodes => prevNodes.map(node => ({
                ...node,
                data: {
                    ...node.data,
                    connectionVersion: connectionVersion + 1
                }
            })));
        }, 0);
    };

    const onEdgesChange = (elements) => {
        elements.forEach((element) => {
            if (element.type === 'remove') {
                const edge = edges.find((e) => e.id === element.id);
                if (edge) {
                    const inputId = edge.targetHandle;
                    setEdgeDeletionList((prev) =>
                        [...prev, { source: edge.source, target: edge.target }]
                    );
                    if (inputId) {
                        setNodes((prevNodes) =>
                            prevNodes.map((node) => {
                                if (node.id !== edge.target) return node;
                                let newData = { ...node.data };
                                if (inputId[1] === '1') {
                                    newData.value_1 = 0;
                                    newData.is_connected_1 = false;
                                    newData.connected_type_1 = null;
                                } else if (inputId[1] === '2') {
                                    newData.value_2 = 0;
                                    newData.is_connected_2 = false;
                                    newData.connected_type_2 = null;
                                }
                                return { ...node, data: newData };
                            })
                        );
                    }
                }
            }
        });

        setEdges((eds) =>
            applyEdgeChanges(elements, eds)
        );

        // Update connection version to trigger validation refresh in action nodes
        setConnectionVersion(prev => prev + 1);

        // Update nodes with new connection version to trigger re-validation
        setTimeout(() => {
            setNodes(prevNodes => prevNodes.map(node => ({
                ...node,
                data: {
                    ...node.data,
                    connectionVersion: connectionVersion + 1
                }
            })));
        }, 0);
    };

    const addVariable = (data) => {
        axios.post(`operation_variable/`, {
            project_id: 1,
            layout_id: currentLayout,
            variable_data_id: data.id,
            variable_id: data.variable.id
        }).then((response) => {
            setNodes((prevNodes) => [
                ...prevNodes,
                {
                    ...response.data,
                    data: {
                        ...response.data.data,
                        setNodes: setNodes,
                        setDeletionList: setDeletionList,
                        onDeleteNode: onDeleteNode,
                    },
                }
            ])
        }).catch((err) => {
            console.error('Error adding variable:', err);
        });
    }

    const addOperation = () => {
        axios.post('operation/', {
            project_id: 1,
            layout_id: currentLayout
        }).then((response) => {
            setNodes((prevNodes) => [
                ...prevNodes,
                {
                    ...response.data,
                    data: {
                        ...response.data.data,
                        setNodes: setNodes,
                        setDeletionList: setDeletionList,
                        onDeleteNode: onDeleteNode,
                    },
                }
            ])
        }).catch((err) => {
            console.error('Error adding operation:', err);
        });
    }

    const addAction = () => {
        console.log('addAction called, currentLayout:', currentLayout);
        axios.post('action/', {
            project_id: 1,
            layout_id: currentLayout
        }).then((response) => {
            console.log('Action created:', response.data);
            setNodes((prevNodes) => [
                ...prevNodes,
                {
                    ...response.data,
                    data: {
                        ...response.data.data,
                        setNodes: setNodes,
                        setDeletionList: setDeletionList,
                        onDeleteNode: onDeleteNode,
                        getResultVariables: getResultVariables,
                        getConnectedOperations: getConnectedOperationsForAction,
                        connectionVersion: connectionVersion,
                    },
                }
            ]);
        }).catch((err) => {
            console.error('Error adding action:', err);
            setNotification({
                show: true,
                type: 'error',
                message: t('operations.sandbox.errorCreatingAction')
            });
        });
    }

    const addTimer = () => {
        axios.post('operation/', {
            project_id: 1,
            layout_id: currentLayout,
            is_timer: true
        }).then((response) => {
            setNodes((prevNodes) => [
                ...prevNodes,
                {
                    ...response.data,
                    type: 'timer',
                    data: {
                        ...response.data.data,
                        operation: 15,
                        setNodes: setNodes,
                        setDeletionList: setDeletionList,
                        onDeleteNode: onDeleteNode,
                    },
                }
            ]);
        }).catch((err) => {
            console.error('Error adding timer:', err);
        });
    }

    const addDelay = () => {
        axios.post('operation/', {
            project_id: 1,
            layout_id: currentLayout,
            is_delay: true
        }).then((response) => {
            setNodes((prevNodes) => [
                ...prevNodes,
                {
                    ...response.data,
                    type: 'delay',
                    data: {
                        ...response.data.data,
                        operation: 14,
                        setNodes: setNodes,
                        setDeletionList: setDeletionList,
                        onDeleteNode: onDeleteNode,
                    },
                }
            ]);
        }).catch((err) => {
            console.error('Error adding delay:', err);
        });
    }

    useEffect(() => {
        let timer;

        if (showSaved) {
            // Set the variable back to false after 3 seconds
            timer = setTimeout(() => {
                setShowSaved(false);
            }, 3000);
        }

        return () => {
            // Clear the timer if the component unmounts or the variable changes before the timer completes
            clearTimeout(timer);
        };
    }, [showSaved]);

    useEffect(() => {
        let timer;

        if (notification.show) {
            // Auto-dismiss notification after 5 seconds
            timer = setTimeout(() => {
                setNotification({ show: false, type: '', message: '' });
            }, 5000);
        }

        return () => {
            clearTimeout(timer);
        };
    }, [notification.show]);

    const testAll = () => {
        if (!operation_group_id) return;
        setTestAllLoading(true);
        axios.post(`operation_group/${operation_group_id}/test_all/`).then(res => {
            setTestAllLoading(false);
            alert(t('operations.sandbox.testComplete', {count: res.data.actions_triggered}));
        }).catch(err => {
            setTestAllLoading(false);
            alert(err.response?.data?.error || 'Test failed');
        });
    };

    const submit = () => {
        // Validate all actions before saving
        const validation = validateAllActions(nodes, edges);

        if (!validation.valid) {
            setValidationErrors(validation.errors);
            // Show notification with validation errors
            const errorMessages = validation.errors.map(e =>
                `${t(e.actionType)}: ${e.message}`
            ).join(' • ');
            setNotification({
                show: true,
                type: 'error',
                message: `${t('operations.sandbox.cannotSave')} ${errorMessages}`
            });
            return;
        }

        setValidationErrors([]);

        // Now send all nodes including result_variables
        // The backend handles processing and saving to its corresponding table

        axios.put('operation/', {
            nodes: nodes,
            edges: edges,
            deletionList: deletionList,
            edgeDeletionList: edgeDeletionList,
            group: operation_group_id,
            layout: currentLayout
        }).then(() => {
            if (isMountedRef.current) {
                setShowSaved(true);
                setDeletionList([]);
            }
        }).catch((err) => {
            console.error('Error saving operations:', err);
        });
    }
    
  return (
      <div style={{ width: '100%', height: '100%', overflowX: 'auto', overflowY: 'auto' }}>
        {notification.show && (
            <div className={`${styles.notification} ${notification.type === 'error' ? styles.notificationError : styles.notificationSuccess}`}>
                <div className={`${styles.notificationIcon} ${notification.type === 'error' ? styles.notificationIconError : styles.notificationIconSuccess}`}>
                    {notification.type === 'error' ? '⚠' : '✓'}
                </div>
                <div className={`${styles.notificationMessage} ${notification.type === 'error' ? styles.notificationMessageError : styles.notificationMessageSuccess}`}>
                    {notification.message}
                </div>
                <button
                    onClick={() => setNotification({ show: false, type: '', message: '' })}
                    className={`${styles.notificationClose} ${notification.type === 'error' ? styles.notificationCloseError : styles.notificationCloseSuccess}`}
                >
                    ×
                </button>
            </div>
        )}
        <OperationsHeader/>
        <div className="d-flex flex-row" style={{height: 'calc(100% - 85px)'}}>
            <OperationsSidebar
                addVariable={addVariable}
                layouts={layouts}
                setCurrentLayout={setCurrentLayout}
                parentFetched={fetched}
                handleDeletion={deletionHandle}
                project={project}
                nodes={nodes}
                addResultVariable={(rv) => {
                    const newNode = {
                        id: `result_${rv.operation_id}_${Date.now()}`,
                        type: 'value',
                        position: { x: 100, y: 100 },
                        data: {
                            label: rv.name,
                            source_operation_id: rv.operation_id,
                            source_layout_id: rv.layout_id,
                            is_result_variable: true,
                            setNodes: setNodes,
                            setDeletionList: setDeletionList,
                            onDeleteNode: onDeleteNode,
                        },
                    };
                    setNodes((prevNodes) => [...prevNodes, newNode]);
                }}
            />

            <ReactFlow
                proOptions={proOptions}
                nodeTypes={nodeTypes}
                nodes={nodes}
                onNodesChange={onNodesChange}
                edges={edges}
                onEdgesChange={onEdgesChange}
                onConnect={handleConnect}
                connectionLineType={"smoothstep"}
            >
                <Controls/>
                <Background variant="dots" gap={12} size={1} />
            </ReactFlow>
            <OperationsToolbar saveAction={submit} addAction={addAction} addOperation={addOperation} addTimer={addTimer} addDelay={addDelay} showSaved={showSaved} devMode={corporateGroup?.dev_mode} onTestAll={testAll} testAllLoading={testAllLoading}/>
        </div>
    </div>
  )
}
